(ns admin.components.composite.resource-selector
  "The resource selector simply provides a way for users to navigate
  a collection of resources and select one. It provides features such
  as search (by filtering), document type filtering, and pagination."
  (:require
   [clojure.string :refer [capitalize lower-case replace]]
   [reagent.core :as reagent]
   [re-frame.core :as re-frame]

   [admin.components.composite.resource-pagination :as resource-pagination]
   [admin.components.composite.learning-objective-pill :as lo-pill]

   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.components.composite.checkbox :as checkbox]
   [kyber.components.composite.resource-icon-preview :as resource-icon-preview]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.utils :as ku]
   [kyber.lib.kyber-db :as kyber-db]))

(def classes
  (styles->classes
   {:resource-container
    {:display :flex
     :align-items :center
     :padding-bottom (gs [:spacing :p8])
     :padding-top (gs [:spacing :p8])
     :width "100%"
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))}

    :details-container
    {:display :flex
     :align-items :center
     :flex 1}

    :header-container
    {:display :flex
     :flex-direction :column
     :padding-bottom (gs [:spacing :p8])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))}

    :header-top
    {:display :flex
     :justify-content :space-between
     :align-items :center}

    :filter-selection
    {:border (str "1px solid " (gs [:colors :gray-scale-200]))
     :margin (gs [:spacing :p8])
     :padding (gs [:spacing :p8])
     :border-radius (gs [:radius :r7])

     "&:hover"
     {:border (str "1px solid " (gs [:colors :gray-scale-400]))}}

    :search-bar-container
    {:display :flex
     :flex-direction :row
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius (gs [:radius :r7])

     :padding [[(gs [:spacing :p12])
                (gs [:spacing :p8])]]

     "&:hover"
     {:border (str "1px solid " (gs [:colors :gray-scale-400]))}

     "& > *"
     {"&:first-child"
      {:margin-right (gs [:spacing :p12])}}}

    :toggle-container
    {:display :flex
     :flex-direction :row

     "& > *"
     {:padding-right (gs [:spacing :p12])

      "&:last-child"
      {:padding-right (gs [:spacing :p0])}}}

    :toggle-item
    {:display :flex
     :flex-direction :column
     :align-items :center}

    :custom-version-id-container
    {:margin-bottom (gs [:spacing :p12])}

    :custom-version-id-input
    {:width "100%"
     :padding (gs [:spacing :p8])
     :border (str "1px solid " (gs [:colors :gray-scale-200]))
     :border-radius (gs [:radius :r3])}

    :custom-version-id-input-error
    {:width "100%"
     :padding (gs [:spacing :p8])
     :border (str "1px solid " (gs [:colors :error-500]))
     :border-radius (gs [:radius :r3])}

    :custom-version-id-error-message
    {:color (gs [:colors :error-500])
     :margin-top (gs [:spacing :p4])
     :margin-bottom (gs [:spacing :p0])}}))

(defonce all-types
  ["video" "audio" "pdf" "image"])

(defn- render-resource
  [{resource-name :resource/name
    resource-description :resource/description
    resource-learning-objective :resource/learning-objective
    :as resource}
   {:keys [on-select cta-copy learning-objectives custom-version-id custom-version-id-error show-custom-version-id?]}]
  (let [learning-objective
        (->> learning-objectives
             (filter #(= (:learning-objective/uuid %)
                         (:learning-objective/uuid resource-learning-objective)))
             (first))]
    [:li {:class (:resource-container classes)
          :data-test "resource-container"}
     [:div {:class (:details-container classes)}
      [resource-icon-preview/render (ku/nsmap->map resource)]
      [:div
       [text/title-10 :p {:style {:word-break :break-all}}
        resource-name]
       [button/button-link
        {:on-click
         #(re-frame/dispatch
           [:resource-library/open-resource-viewer resource])}
        "Preview"]
       [text/body-20 :p {:style {:word-break :break-all}}
        resource-description]]]

     (when-let [label (:learning-objective/label learning-objective)]
       [lo-pill/render {:label label}])

     (when (fn? on-select)
       [button/secondary-button
        {:on-click #(do
                      (.preventDefault %)
                      (if (and show-custom-version-id? (empty? @custom-version-id))
                        (reset! custom-version-id-error "Custom ID is required")
                        (do
                          (reset! custom-version-id-error nil)
                          (on-select resource @custom-version-id))))
         :data-test "add-resource-button"
         :style {:margin-left (gs [:spacing :p20])}}
        cta-copy])]))

(defn render
  [_]
  (let [current-page (reagent/atom 1)
        search-term (reagent/atom "")
        custom-version-id (reagent/atom "")
        custom-version-id-error (reagent/atom nil)
        search-types-atom (reagent/atom {:video false
                                         :audio false
                                         :pdf false
                                         :image false})]
    (fn [{:keys [accepted-file-types
                 learning-objectives
                 resources
                 on-select
                 per-page
                 cta-copy
                 show-search-and-filter?
                 activity-uuid
                 show-custom-version-id?]
          :or {accepted-file-types (set (map keyword all-types))
               per-page 6
               cta-copy "Add"
               show-search-and-filter? true
               show-custom-version-id? false}}]
      (let [selected-types
            (reduce (fn [acc [file-type is-selected]]
                      (if (true? is-selected)
                        (conj acc file-type)
                        acc))
                    #{}
                    @search-types-atom)
            
            escaped-search-term (replace @search-term #"[\*\\\"\(\)\[\?\+]" #(str "\\" %))


            {primary-learning-object :activity/primary-learning-object}
            @(re-frame/subscribe
              [::kyber-db/get-record
               {:model :activity
                :ident activity-uuid}])
                        
            plo-uuid (:resource/uuid primary-learning-object)

            filtered-resources
            (cond->> resources
              :always
              (filter #(contains? accepted-file-types
                                  (-> % :resource/file-type keyword)))

              (some? plo-uuid)
              (filter #(not (= (:resource/uuid %) plo-uuid)))

              (seq selected-types)
              (filter #(contains? selected-types
                                  (-> % :resource/file-type keyword)))

              (and (seq @search-term)
                   (seq resources))
              (filter #(seq (re-find (re-pattern (str "(?i)" escaped-search-term))
                                     (:resource/name %))))

              :always
              (sort-by #(-> % :resource/name (or "") lower-case))) 
              ;; (or "") prevents resource page from breaking if blank resource is created.

            search-types
            @search-types-atom

            resource-count
            (count filtered-resources)

            paginated-resources
            (subvec (vec filtered-resources)
                    (* per-page (dec @current-page))
                    (min (* per-page @current-page)
                         resource-count))]

        [:<>
         (when show-search-and-filter?
           [:div {:class (:header-container classes)}
            ;; Custom ID field for PLO uploads - moved to top
            (when show-custom-version-id?
              [:div {:class (:custom-version-id-container classes)}
               [:input
                {:type :text
                 :value @custom-version-id
                 :on-change #(do
                               (reset! custom-version-id (-> % .-target .-value))
                               (when @custom-version-id-error
                                 (reset! custom-version-id-error nil)))
                 :placeholder "Enter Custom ID..."
                 :class (if @custom-version-id-error
                          (:custom-version-id-input-error classes)
                          (:custom-version-id-input classes))}]
               (when @custom-version-id-error
                 [text/caption-10 :p
                  {:class (:custom-version-id-error-message classes)}
                  @custom-version-id-error])])

            [:div {:class (:header-top classes)}
             [:div
              [text/caption-20 :p
               "Choose a file from the Resource Library..."]
              [text/caption-10 :p
               "Sorted by title (A - Z)"]]
             (when (< 1 (count accepted-file-types))
               [:div {:class (:filter-selection classes)}
                [text/title-20 :p "Filter by file type"]
                [:div {:class (:toggle-container classes)}
                 (for [type (vec accepted-file-types)]
                   ^{:key type}
                   [:div {:class (:toggle-item classes)}
                    [text/caption-20 :p (capitalize (name type))]
                    [checkbox/render
                     {:name type
                      :style {:margin-right (gs [:spacing :p0])
                              :color
                              (gs [:colors :primary-lite-600])}
                      :on-click #(do
                                   (swap! search-types-atom
                                          assoc
                                          type
                                          (not
                                           (search-types type)))
                                   (reset! current-page 1))
                      :selected (search-types type)}]])]])]

            [:div {:class (:search-bar-container classes)}
             [:input
              {:type :text
               :value @search-term
               :on-change #(do
                             (reset! current-page 1)
                             (reset! search-term (-> %
                                                     .-target
                                                     .-value)))
               :placeholder "Enter search term here..."}]
             [button/icon-button (cond-> {:on-click
                                          #(do
                                             (.preventDefault %)
                                             (reset! search-term ""))}
                                   (empty? @search-term)
                                   (assoc :disabled true))
              :times]]])
               
         [:ul {:data-test "resources-container"}
          (for [resource paginated-resources]
            ^{:key (:resource/uuid resource)}
            [render-resource resource
             {:on-select on-select
              :cta-copy cta-copy
              :learning-objectives learning-objectives
              :custom-version-id custom-version-id
              :custom-version-id-error custom-version-id-error
              :show-custom-version-id? show-custom-version-id?}])]

         (when (and (or (seq @search-term)
                        (seq selected-types))
                    (zero? resource-count))
           [text/title-20 :p {:style {:text-align :center}}
            "No resources to display! Remove some filters and try again."])

         (when (< per-page (count filtered-resources))
           [resource-pagination/render {:current-page current-page
                                        :per-page per-page
                                        :resource-count resource-count}])]))))
